import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

import { ensureFilipinoResources } from '../../i18n';

type LanguageSwitcherProps = {
  /** In header toolbar: icon-only until xl to save horizontal space on laptops. */
  variant?: 'default' | 'toolbar';
};

const LanguageSwitcher = ({ variant = 'default' }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fil', name: 'Filipino' }
  ];
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  
  const changeLanguage = async (languageCode: string) => {
    if (languageCode === 'fil') {
      await ensureFilipinoResources();
    }
    await i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };
  
  const menuId = 'language-switcher-options';

  return (
    <div className="relative">
      <button
        type="button"
        className={`flex items-center rounded-lg text-sm font-medium text-neutral-800 hover:bg-neutral-100 hover:text-primary-700 ${
          variant === 'toolbar'
            ? 'gap-0 px-2 py-2 xl:gap-1.5 xl:px-2 xl:py-1.5'
            : 'gap-1.5 px-2 py-1.5'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-label={`${t('language.switcherLabel', 'Change language')}: ${currentLanguage.name}`}
      >
        <Globe className="h-[18px] w-[18px] shrink-0 stroke-[1.85]" aria-hidden />
        <span className={variant === 'toolbar' ? 'hidden xl:inline' : ''}>{currentLanguage.name}</span>
      </button>
      
      {isOpen && (
        <div
          id={menuId}
          className="absolute right-0 z-50 mt-2 w-40 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
          role="listbox"
          aria-label={t('language.selectLabel', 'Select language')}
        >
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => changeLanguage(language.code)}
              className={`block w-full px-4 py-2 text-left text-sm ${
                i18n.language === language.code
                  ? 'bg-primary-50 font-medium text-primary-800'
                  : 'text-neutral-800 hover:bg-neutral-100'
              }`}
              role="option"
              aria-selected={i18n.language === language.code}
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;